/**
 * What the offline layer knows about the API's resources: which changes can be
 * made offline, how the server would create a document, and how a change
 * shows up in the cached GET responses (e.g. a new task goes into its
 * section's `tasks` array in GET /api/tasks).
 */

export const OBJECT_ID = /^[0-9a-f]{24}$/i;

const RESOURCES = new Set([
  "tasks",
  "sections",
  "boards",
  "notes",
  "noteGroups",
  "todolists",
  "postits",
]);

// Updates of these are revision-checked, so offline changes to them can be
// merged with changes made on the server (server/utils/revision.js)
export const REVISIONED = new Set(["tasks", "notes", "todolists", "postits"]);

// Resources that have a UUID `id` next to `_id`
const HAS_UUID = new Set([
  "tasks",
  "sections",
  "boards",
  "notes",
  "noteGroups",
]);

// Same defaults as server/models/Board.js
const DEFAULT_STATUS_CONFIG = [
  { label: "Not Started", color: "#797e93", order: 0 },
  { label: "In Progress", color: "#fdbc65", order: 1 },
  { label: "Completed", color: "#34d491", order: 2 },
  { label: "Stuck", color: "#e8697d", order: 3 },
  { label: "Pending", color: "#2382e4", order: 4 },
];
const DEFAULT_PRIORITY_CONFIG = [
  { label: "Urgent", color: "#e8697d", order: 0 },
  { label: "High", color: "#fdbc65", order: 1 },
  { label: "Medium", color: "#2382e4", order: 2 },
  { label: "Low", color: "#34d491", order: 3 },
  { label: "Trivial", color: "#797e93", order: 4 },
];

const randomHex = (bytes) =>
  Array.from(window.crypto.getRandomValues(new Uint8Array(bytes)), (b) =>
    b.toString(16).padStart(2, "0"),
  ).join("");

// A new MongoDB ObjectId: 4 bytes timestamp + 8 random bytes
export const newObjectId = () =>
  Math.floor(Date.now() / 1000)
    .toString(16)
    .padStart(8, "0") + randomHex(8);

const newUuid = () => {
  if (window.crypto.randomUUID) return window.crypto.randomUUID();
  const hex = randomHex(16);
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-4${hex.slice(13, 16)}-a${hex.slice(17, 20)}-${hex.slice(20)}`;
};

/**
 * Describe a request that may be made offline
 * @returns {Object|null} { kind: "create"|"update"|"delete"|"reorder", resource, id? }
 *   or null for requests that need a connection (uploads, bulk deletes, ...)
 */
export const describeMutation = (method, path) => {
  const [api, resource, ...rest] = path.split("/").filter(Boolean);
  if (api !== "api" || !RESOURCES.has(resource)) return null;

  if (method === "POST" && rest.length === 0) {
    return { kind: "create", resource };
  }
  if (method === "PUT" && rest[0] === "reorder") {
    return { kind: "reorder", resource };
  }
  if (rest.length === 1 && OBJECT_ID.test(rest[0])) {
    if (method === "PUT") return { kind: "update", resource, id: rest[0] };
    if (method === "DELETE") return { kind: "delete", resource, id: rest[0] };
  }
  return null;
};

// Give a create request client-generated IDs, so it can be retried safely
// and later changes can refer to the new document
export const withNewIds = (resource, body) => ({
  ...body,
  _id: newObjectId(),
  ...(HAS_UUID.has(resource) ? { id: newUuid() } : {}),
});

// Give new subdocuments (e.g. list items) IDs, so they can be changed before
// the server has seen them
export const withItemIds = (body) => {
  if (!Array.isArray(body?.items)) return body;
  return {
    ...body,
    items: body.items.map((item) =>
      item && typeof item === "object" && !item._id
        ? { ...item, _id: newObjectId() }
        : item,
    ),
  };
};

// The { _id, sortOrder, ... } entries of a reorder request
export const reorderEntries = (body) => {
  const entries = Array.isArray(body) ? body : body?.updates;
  return Array.isArray(entries)
    ? entries.filter((entry) => entry && entry._id)
    : [];
};

// An update's changed fields (its `rev` is only for the server's check)
export const changedFields = (body) => {
  const fields = { ...body };
  delete fields.rev;
  return fields;
};

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

/**
 * The document the server would create for a create request
 * @param {String} resource
 * @param {Object} body - Request body (with client IDs)
 * @param {Object} ctx - { entities: Map of known documents by _id, userId }
 */
export const synthesizeCreate = (resource, body, { entities, userId }) => {
  const now = new Date().toISOString();
  const all = [...entities.values()];
  const findEntity = (id) =>
    all.find((e) => sameId(e._id, id) || sameId(e.id, id));
  const maxSortOrder = (items) =>
    items.reduce((max, e) => Math.max(max, Number(e.sortOrder) || 0), -1);

  switch (resource) {
    case "tasks": {
      const section = findEntity(body.groupId);
      const sectionId = section?._id || body.groupId;
      const siblings = all.filter(
        (e) => sameId(e.sectionId, sectionId) && e.title !== undefined,
      );
      const sortOrder =
        body.position === "top"
          ? siblings.reduce(
              (min, e) => Math.min(min, Number(e.sortOrder) || 0),
              1,
            ) - 1
          : maxSortOrder(siblings) + 1;
      return {
        _id: body._id,
        id: body.id,
        title: body.title,
        responsible: body.responsible ?? "",
        status: body.status ?? "Not Started",
        description: body.description ?? "",
        isActive: body.isActive ?? true,
        priority: 5,
        sortOrder,
        sectionId,
        parentTaskId: body.parentTaskId ?? null,
        isSubtask: body.isSubtask ?? false,
        links: [],
        images: [],
        createdAt: now,
        rev: 0,
      };
    }
    case "sections":
    case "noteGroups":
      return {
        _id: body._id,
        id: body.id,
        projectId: body.projectId,
        ...(resource === "sections" ? { boardId: body.boardId || null } : {}),
        name: body.name,
        color: body.color,
        sortOrder: body.sortOrder,
        isActive: true,
        createdAt: now,
      };
    case "boards":
      return {
        _id: body._id,
        id: body.id,
        projectId: body.projectId,
        name: body.name,
        color: body.color,
        icon: body.icon,
        sortOrder: body.sortOrder ?? 0,
        statusConfig: DEFAULT_STATUS_CONFIG,
        priorityConfig: DEFAULT_PRIORITY_CONFIG,
        isActive: true,
        createdAt: now,
        updatedAt: now,
      };
    case "notes": {
      const group = findEntity(body.groupId);
      const noteGroupId = group?._id || body.groupId;
      return {
        _id: body._id,
        id: body.id,
        title: body.title,
        content: body.content ?? "",
        links: body.links ?? [],
        images: [],
        color: body.color,
        isActive: body.isActive ?? true,
        sortOrder: all.filter((e) => sameId(e.noteGroupId, noteGroupId)).length,
        noteGroupId,
        createdAt: now,
        rev: 0,
      };
    }
    case "todolists": {
      const scope = body.scope || "personal";
      const projectId = scope === "project" ? body.projectId : null;
      const siblings = all.filter(
        (e) =>
          Array.isArray(e.items) &&
          e.scope === scope &&
          (scope !== "project" || sameId(e.projectId, projectId)),
      );
      return {
        _id: body._id,
        title: body.title || "New Todo List",
        color: body.color || "#2196f3",
        type: body.type || "todo",
        scope,
        projectId,
        items: [],
        userId,
        sortOrder: maxSortOrder(siblings) + 1,
        createdAt: now,
        updatedAt: now,
        rev: 0,
      };
    }
    case "postits":
      return {
        _id: body._id,
        userId,
        content: body.content || "",
        color: body.color || "#f2dd3d",
        position: body.position || { x: 0, y: 0 },
        zIndex: body.zIndex || 1,
        isPinned: false,
        createdAt: now,
        updatedAt: now,
        rev: 0,
      };
    default:
      return { ...body };
  }
};

// ---- Applying changes to cached GET responses ----

// Call visit(object) for every object in a JSON value
const forEachObject = (node, visit) => {
  if (Array.isArray(node)) {
    node.forEach((item) => forEachObject(item, visit));
  } else if (node && typeof node === "object") {
    visit(node);
    Object.values(node).forEach((value) => forEachObject(value, visit));
  }
};

// Call visit(array) for every array in a JSON value
const forEachArray = (node, visit) => {
  if (Array.isArray(node)) {
    visit(node);
    node.forEach((item) => forEachArray(item, visit));
  } else if (node && typeof node === "object") {
    Object.values(node).forEach((value) => forEachArray(value, visit));
  }
};

// Every document (object with an ObjectId `_id`) in a response, by _id
export const collectEntities = (data, entities) => {
  forEachObject(data, (object) => {
    if (typeof object._id === "string" && OBJECT_ID.test(object._id)) {
      entities.set(object._id, object);
    }
  });
};

export const findInData = (data, id) => {
  let found = null;
  forEachObject(data, (object) => {
    if (!found && sameId(object._id, id)) found = object;
  });
  return found;
};

const patchEntity = (data, id, fields) => {
  if (data && !Array.isArray(data) && sameId(data._id, id)) {
    Object.assign(data, fields);
  }
  forEachObject(data, (object) => {
    if (sameId(object._id, id) && object !== data)
      Object.assign(object, fields);
  });
};

const removeEntity = (data, id) => {
  forEachArray(data, (array) => {
    for (let i = array.length - 1; i >= 0; i--) {
      if (sameId(array[i]?._id, id)) array.splice(i, 1);
    }
  });
};

// Move children (tasks/notes) whose parent ID changed to their new parent
const reparent = (data, childKey, parentField) => {
  const parents = [];
  forEachObject(data, (object) => {
    if (Array.isArray(object[childKey])) parents.push(object);
  });
  parents.forEach((parent) => {
    const moving = parent[childKey].filter(
      (child) =>
        child?.[parentField] && !sameId(child[parentField], parent._id),
    );
    if (!moving.length) return;
    parent[childKey] = parent[childKey].filter((c) => !moving.includes(c));
    moving.forEach((child) => {
      const target = parents.find((p) => sameId(p._id, child[parentField]));
      if (target) target[childKey].push(child);
    });
  });
};

const sortBySortOrder = (array) => {
  if (array.every((item) => typeof item?.sortOrder === "number")) {
    array.sort((a, b) => a.sortOrder - b.sortOrder);
  }
};

// Insert a document created offline into the cached response of `url`
const insertCreated = (resource, data, url, doc) => {
  const { pathname, searchParams } = url;
  const projectId = searchParams.get("projectId");
  const matchesProject = sameId(projectId, doc.projectId);

  if (!Array.isArray(data)) return;

  switch (resource) {
    case "tasks":
      if (pathname === "/api/tasks") {
        const section = data.find((s) => sameId(s._id, doc.sectionId));
        if (section?.tasks) section.tasks.push(doc);
      }
      break;
    case "sections": {
      const boardId = searchParams.get("boardId");
      if (
        pathname === "/api/tasks" &&
        matchesProject &&
        (!boardId || sameId(boardId, doc.boardId))
      ) {
        data.push({ ...doc, tasks: [] });
      }
      break;
    }
    case "notes":
      if (pathname === "/api/notes") {
        const group = data.find((g) => sameId(g._id, doc.noteGroupId));
        if (group?.notes) group.notes.push(doc);
      }
      break;
    case "noteGroups":
      if (pathname === "/api/notes" && matchesProject) {
        data.push({ ...doc, notes: [] });
      }
      break;
    case "boards":
      if (pathname === "/api/boards" && matchesProject) data.push(doc);
      break;
    case "todolists": {
      const isPersonalList =
        pathname === "/api/todolists" || pathname === "/api/todolists/personal";
      const projectMatch = pathname.match(/^\/api\/todolists\/project\/(\w+)$/);
      if (
        (isPersonalList && doc.scope === "personal") ||
        (projectMatch &&
          doc.scope === "project" &&
          sameId(projectMatch[1], doc.projectId))
      ) {
        data.push(doc);
      }
      break;
    }
    case "postits":
      if (pathname === "/api/postits") data.unshift(doc);
      break;
    default:
      break;
  }
};

/**
 * Apply queued offline changes to a (copy of a) cached GET response, so pages
 * show what was changed offline
 * @param {Object|Array} data - Response JSON; modified in place
 * @param {URL} url - The GET request's URL
 * @param {Array} outbox - Queued changes
 */
export const applyPendingChanges = (data, url, outbox) => {
  const showCompleted = url.searchParams.get("showCompleted") === "true";

  outbox.forEach((op) => {
    if (op.kind === "create") {
      insertCreated(op.resource, data, url, structuredClone(op.doc));
    } else if (op.kind === "update") {
      const fields = changedFields(op.body);
      patchEntity(data, op.id, fields);
      const entity = findInData(data, op.id);
      if (
        fields.isActive === false &&
        !showCompleted &&
        entity &&
        !entity.isSubtask
      ) {
        removeEntity(data, op.id);
      }
    } else if (op.kind === "delete") {
      removeEntity(data, op.id);
    } else if (op.kind === "reorder") {
      reorderEntries(op.body).forEach(({ _id, ...fields }) =>
        patchEntity(data, _id, fields),
      );
    }
  });

  reparent(data, "tasks", "sectionId");
  reparent(data, "notes", "noteGroupId");

  if (Array.isArray(data)) {
    sortBySortOrder(data);
    data.forEach((item) => {
      if (Array.isArray(item?.tasks)) sortBySortOrder(item.tasks);
      if (Array.isArray(item?.notes)) sortBySortOrder(item.notes);
    });
  }
  return data;
};
